/**
 * The segmented control — one choice among a few, all visible (Trends' grain,
 * a theme, a period length).
 *
 * A radiogroup, not a row of toggle buttons: exactly one is always chosen, and
 * arrow keys move the choice (W-8), as a screen reader expects of a radiogroup.
 * A pill of hairline with a tinted selection — a control, not a container, so
 * D-1 is not in play.
 */

import { useHaptics } from "@/hooks/use_haptics";
import { useRef } from "react";

import { cx } from "@/features/common/primitives";

export interface Segment<T extends string> {
  value: T;
  label: string;
  disabled?: boolean;
}

export function Segmented<T extends string>({
  label,
  segments,
  value,
  onChange,
}: {
  /** Names the group for assistive technology. */
  label: string;
  segments: readonly Segment<T>[];
  value: T;
  onChange: (next: T) => void;
}): React.JSX.Element {
  const haptic = useHaptics();
  const change = (next: T): void => { if (next !== value && !segments.find(segment => segment.value === next)?.disabled) { haptic("select"); onChange(next); } };
  const buttons = useRef<(HTMLButtonElement | null)[]>([]);

  const move = (from: number, step: number): void => {
    let next = (from + step + segments.length) % segments.length;
    for (let count = 0; count < segments.length && segments[next]?.disabled; count++) next = (next + step + segments.length) % segments.length;
    const segment = segments[next];
    if (!segment) return;
    change(segment.value);
    buttons.current[next]?.focus();
  };

  const selectedIndex = segments.findIndex(segment => segment.value === value);
  return (
    <div role="radiogroup" aria-label={label} className="rounded-full border border-line p-phi-n8">
      <div className="relative isolate flex rounded-full">
      {selectedIndex >= 0 ? <span aria-hidden="true" className="feel-segment-fill" style={{ width: `${100 / segments.length}%`, transform: `translateX(${selectedIndex * 100}%)` }} /> : null}
      {segments.map((segment, index) => {
        const checked = segment.value === value;
        return (
          <button
            key={segment.value}
            ref={(el) => {
              buttons.current[index] = el;
            }}
            type="button"
            role="radio"
            aria-checked={checked}
            disabled={segment.disabled}
            tabIndex={checked ? 0 : -1}
            onClick={() => change(segment.value)}
            onKeyDown={(event) => {
              if (event.key === "ArrowRight" || event.key === "ArrowDown") move(index, 1);
              else if (event.key === "ArrowLeft" || event.key === "ArrowUp") move(index, -1);
              else return;
              event.preventDefault();
            }}
            className={cx(
              "min-h-[44px] flex-1 rounded-full px-phi-n1 text-phi-n1 disabled:opacity-35",
              checked ? "font-bold text-tx" : "text-tx-2",
            )}
          >
            {segment.label}
          </button>
        );
      })}
      </div>
    </div>
  );
}
